import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import ReactPlayer from 'react-player';
import React, { useEffect, useState } from 'react';
import { Tabs } from '@cloudscape-design/components';
import DOMPurify from 'dompurify';

type useRenderContentProps = {
    data: string;
    artifactId: string;
    setModalHref: (href: string) => void;
    loading: boolean;
};
export const useRenderContent = ({
    artifactId,
    data,
    loading,
    setModalHref,
}: useRenderContentProps) => {
    const [artifactHtml, setArtifactHtml] = useState(parse(''));
    const linkElementsRef = React.useRef<HTMLLinkElement[]>([]);

    useEffect(() => {
        if (loading) {
            setArtifactHtml(parse(''));
        }
    }, [loading]);

    const findTabInformation = (child: any, dataTab = ''): any => {
        if (!child.children || !child.children.length) {
            return { child, dataTab };
        }

        const data_tab = child.attribs['data-tab'] || dataTab;
        const nextChoice = child.attribs.role === 'tab' ? 1 : 0;
        return findTabInformation(child.children[nextChoice], data_tab);
    };
    const options: HTMLReactParserOptions = {
        replace: (node: any) => {
            // This is all hidden or display none. Not sure why it's delivered
            if (node.name === 'script') {
                return <></>;
            }
            if (
                node.attribs &&
                node.attribs.class &&
                (node.attribs.class === 'FragmentSkinComponentWrapper' ||
                    node.attribs.class === 'LoadImgPlaceholder')
            ) {
                return <></>;
            }
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('HideUntilLoad')
            ) {
                node.attribs.class = node.attribs.class.replace(
                    'HideUntilLoad',
                    ''
                );
                node.attribs.id = `${node.attribs.id}-1`;
            }
            if (
                node.attribs &&
                (node.attribs.class === 'Main' ||
                    node.attribs.class == 'OuterWrap')
            ) {
                node.attribs.class = 'Removed';
                node.attribs.id = `${node.attribs.id}-1`;
            }

            if (
                node.name === 'video' &&
                node.attribs &&
                node.attribs['data-src']
            ) {
                return <ReactPlayer url={node.attribs['data-src']} controls />;
            }

            // Xyleme has some custom code to change display:none to block that we do not
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('OverlayObjects')
            ) {
                node.attribs.style = 'display: block';
            }
            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('Xref')
            ) {
                const href = node.attribs.href;
                node.attribs.href = `/${artifactId}#/${href}`;
                return (
                    <a
                        className={node.attribs.class}
                        onClick={() => {
                            setModalHref(href);
                        }}
                    >
                        {node.children[0]?.data}
                    </a>
                );
            }

            if (
                node.attribs &&
                node.attribs.class &&
                node.attribs.class.includes('Tabs TabsComponent ')
            ) {
                const tabInformation = node.children[0].children.map(
                    (child: any) => {
                        return findTabInformation(child);
                    }
                );
                const tabs = tabInformation.map((tab: any, index: number) => {
                    const content = node.children[1].children[index];
                    return {
                        label: tab.child.data,
                        id: tab.dataTab,
                        content: domToReact(
                            content.children[1].children,
                            options
                        ),
                    };
                });
                return <Tabs tabs={tabs} />;
            }
        },
    };

    useEffect(() => {
        if (data) {
            const matches =
                data.match(
                    /<link[^>]*?(rel=["']stylesheet["']|type=["']text\/css["'])[^>]*?>/gi
                ) || [];

            matches.forEach((match) => {
                const hrefMatch = match.match(/href=["']([^"']+)["']/i);
                if (hrefMatch) {
                    const linkElement = document.createElement('link');
                    linkElement.rel = 'stylesheet';
                    linkElement.href = hrefMatch[1];
                    linkElement.type = 'text/css';
                    document.head.appendChild(linkElement);
                    linkElementsRef.current.push(linkElement);
                }
            });

            const body = data
                .replace(/<!DOCTYPE[^>]*>/i, '')
                .replace(/<\/?html[^>]*>/gi, '')
                .replace(/<head[\s\S]*?<\/head>/gi, '');

            const cleanedHTML = DOMPurify.sanitize(body, {
                ADD_TAGS: ['iframe'],
                ADD_ATTR: ['data-src'],
            });

            setArtifactHtml(parse(cleanedHTML, options));

            return () => {
                linkElementsRef.current.forEach((el) =>
                    document.head.removeChild(el)
                );
                linkElementsRef.current = [];
            };
        }
    }, [data]);

    return { artifactHtml };
};
